import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';

function ConfirmDialog({ enquiry, onDone, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [sponsorName, setSponsorName] = useState(enquiry.sponsor_name);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/confirm`, { sponsor_name_override: sponsorName, sponsorship_start_date: startDate });
      toast.success(`${enquiry.animal_name} is now Sponsored.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm.');
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] p-6 border border-border" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs uppercase  text-muted-foreground">Confirm sponsorship</p>
        <h3 className="font-extrabold text-xl mt-1">{enquiry.animal_name}</h3>
        <div className="space-y-3 mt-4">
          <div>
            <label className="text-sm text-muted-foreground">Sponsor name (as displayed)</label>
            <input value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-transparent px-3 h-10 text-sm" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-transparent px-3 h-10 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="rounded-xl bg-[#1E3A2F] hover:bg-[#1E3A2F]/90">
            {loading ? 'Confirming…' : 'Confirm sponsorship'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setData(data);
    } catch (e) { console.error('Failed to load dashboard:', e); toast.error('Failed to load dashboard.'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decline = async (enquiry) => {
    if (!window.confirm(`Decline enquiry for ${enquiry.animal_name}? Animal will return to Available.`)) return;
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/decline`);
      toast.success('Enquiry declined.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const kpis = [
    { label: 'Available', value: data.available, color: 'text-[#1E3A2F]' },
    { label: 'Reserved', value: data.reserved, color: 'text-[#5C4C1F]' },
    { label: 'Sponsored', value: data.sponsored, color: 'text-[#5C4C1F]' },
    { label: 'Total animals', value: data.horses + data.ponies, color: 'text-muted-foreground' },
  ];

  return (
    <div data-testid={ADMIN.dashboard} className="space-y-8 max-w-5xl">
      <div>
        <p className="text-xs uppercase  text-muted-foreground">Overview</p>
        <h1 className="font-extrabold text-3xl mt-1">Stable Office</h1>
      </div>

      <div data-testid={ADMIN.kpiCards} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="rounded-2xl p-5 border-border bg-[hsl(var(--card))]">
            <p className="text-xs  text-muted-foreground">{k.label}</p>
            <p className={`font-extrabold text-4xl mt-2 ${k.color}`}>{k.value}</p>
          </Card>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-extrabold text-2xl">Pending enquiries</h2>
          <Link to="/admin/enquiries" className="text-sm link-quiet underline-offset-4 hover:underline flex items-center gap-1">
            All enquiries <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {data.pending_enquiries.length === 0 ? (
          <Card className="rounded-2xl p-8 text-center text-muted-foreground border-border">No pending enquiries at the moment.</Card>
        ) : (
          <div data-testid={ADMIN.enquiryQueue} className="space-y-3">
            {data.pending_enquiries.map((e) => (
              <Card key={e.id} className="rounded-2xl p-4 sm:p-5 border-border">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-extrabold text-lg">{e.animal_name}</p>
                      <Badge variant="outline" className="border-[#E4DDC5] text-[#5C4C1F]">Pending</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{e.sponsor_name} · Member #{e.membership_number} · {e.email}</p>
                    {e.message && <p className="text-sm mt-2 italic">“{e.message}”</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={ADMIN.enquiryConfirmButton} onClick={() => setConfirmTarget(e)} size="sm" className="rounded-lg bg-[#1E3A2F] hover:bg-[#1E3A2F]/90">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm
                    </Button>
                    <Button data-testid={ADMIN.enquiryDeclineButton} onClick={() => decline(e)} size="sm" variant="outline" className="rounded-lg">
                      <XCircle className="h-4 w-4 mr-1.5" /> Decline
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {confirmTarget && (
        <ConfirmDialog
          enquiry={confirmTarget}
          onCancel={() => setConfirmTarget(null)}
          onDone={() => { setConfirmTarget(null); load(); }}
        />
      )}
    </div>
  );
}
